import { EditorClass } from "luxedo-canvas"
import { ImageAsset, MaskBase, Polygon, TrackMask } from "luxedo-canvas/asset"
import Mode, { type ModeOptions } from "luxedo-canvas/modules/mode"
import { Track, TrackGroup } from "luxedo-canvas/tracks"
import { MaskController } from "luxedo-canvas/controllers"

export interface ClickForegroundOptions extends ModeOptions {
	masks: Array<MaskBase>
	imageUrl: string
	resetOnComplete?: boolean
	hideBeforeExport?: Array<Track>
}

export class ClickForegroundMode extends Mode<ClickForegroundOptions> {
	masks: Array<MaskBase>
	result: string
	cancellable: boolean = true

	imageUrl: string // path to snapshot (this gets added as an asset)

	showInsideTrack: Track
	showOutsideTrack: TrackGroup
	backgroundShapeTrack: Track

	shapeRendered: boolean

	resetOnComplete: boolean
	hideBeforeExport: Array<Track>

	get modeTitle(): string {
		return "Click foreground"
	}

	/**
	 * Applies event handlers and does a few changes to masks.
	 */
	protected onActivate(options: ClickForegroundOptions): void {
		this.masks = options.masks
		this.masks.forEach((mask) => {
			mask.hoverCursor = "cursor"
			mask.register()
			mask.strokeWidth = 0
			mask.path.strokeWidth = 0
		})

		this.imageUrl = options.imageUrl
		this.hideBeforeExport = options.hideBeforeExport ?? []
		this.resetOnComplete = options.resetOnComplete ?? false

		for (const track of this.hideBeforeExport) {
			if (track.parent === null) {
				this.editor.tracks.attach(track)
			}
			if (track.hidden) track.hidden = false
			if (track.asset instanceof Polygon) {
				track.asset.stroke = "#faae1b"
				track.asset.strokeWidth = 5
			}
		}

		this.editor.canvas.on("mouse:down", this.canvasClick)
		this.createBaseLayers()

		// @ts-ignore this mode doesn't count as a mode apparently
		this.editor.selection.lock(this as Mode<any>)
	}

	/**
	 * Clears the event handlers
	 */
	protected onDeactivate(): void {
		this.showInsideTrack.parent.delete(this.showInsideTrack)
		this.showOutsideTrack.parent.delete(this.showOutsideTrack)
		this.editor.canvas.off("mouse:down", this.canvasClick)
	}

	private async exportImage(): Promise<string> {
		return new Promise<string>((res) => {
			for (const track of this.hideBeforeExport) {
				try {
					track.hidden = true
				} catch (e) {
					console.error("Error hiding track")
					console.error(e)
					console.error(track)
				}
			}

			this.editor.canvas.requestRenderAll()

			setTimeout(() => {
				const img = this.editor.canvas.exportImage("png", true)
				return res(img)
			})
		})
	}

	/**
	 * Identifies if the click was inside or outside of the mask and adds a shape to "mask" the rest of the image with black.
	 */
	protected canvasClick = async (e: fabric.IEvent<Event>) => {
		if (e.button != 1) return

		const { inside, outside } = this.identifyClickedMaskPositions(e.absolutePointer)

		let outsideMask = MaskController.Intersect(outside)

		const insideMasks = [...inside]
		if (outsideMask) insideMasks.push(outsideMask)
		let insideMask = MaskController.Intersect(insideMasks)

		// this.hideOutside(insideMask)
		if (outsideMask) this.hideInside(outsideMask)
		if (insideMask) this.hideOutside(insideMask)

		const img = await this.exportImage()
		this.result = img

		this.complete(this.result)
	}

	/**
	 * Identifies the masks this click was inside of and the masks it was outside of
	 * @returns {
	 * 	inside: an array of track masks
	 * 	outside: an array of track masks
	 * }
	 */
	private identifyClickedMaskPositions(point: fabric.Point): {
		inside: Array<TrackMask>
		outside: Array<TrackMask>
	} {
		const inside: Array<MaskBase> = []
		const outside: Array<MaskBase> = []

		for (const mask of this.masks) {
			const isInside = isPointWithin(point, mask.absolutePoints)
			if (isInside) inside.push(mask)
			else outside.push(mask)
		}

		return {
			inside: inside.map((base) => new TrackMask([base])),
			outside: outside.map((base) => MaskController.Not(new TrackMask([base]))),
		}
	}

	/**
	 * Creates a few needed tracks:
	 * 1. A track with a black shape - this serves as the background of the canvas
	 * 2. A track containing the snapshot as an image - this is required to be able to mask properly
	 * 3. A group containing the the track with the snapshot - this will also have masks applied to it
	 * Tracks #2 and #3 combined allow for masks within masks to be applied to the snapshot image.
	 */
	private createBaseLayers = async () => {
		// create the image and wait for it to load
		const imgAsset = new ImageAsset(
			this.editor,
			{
				mediaId: -1,
				src: this.imageUrl,
			},
			{
				top: this.editor.sceneHeight / 2,
				left: this.editor.sceneWidth / 2,
			}
		)

		await imgAsset.loadingPromise

		// create the black background shape
		const backgroundShape = new Polygon(
			this.editor,
			{
				height: this.editor.sceneHeight,
				width: this.editor.sceneWidth,
				numSides: 4,
			},
			{
				fill: "black",
				stroke: "transparent",
				top: this.editor.sceneHeight / 2,
				left: this.editor.sceneWidth / 2,
				selectable: false,
			}
		)
		backgroundShape.hoverCursor = "cursor"

		// create a track from the snapshot image
		const snapshotImageTrack = this.editor.tracks.addAsset(imgAsset, {
			name: "Snapshot track",
			start: 0,
			end: this.editor.timeline.duration,
			index: 0,
		})
		imgAsset.hoverCursor = "cursor"
		imgAsset.selectable = false

		// put that track in a group
		const showOutsideTrack = snapshotImageTrack.insertParentGroup(
			{
				name: "Group track",
			},
			0
		)

		this.showOutsideTrack = showOutsideTrack
		this.showInsideTrack = snapshotImageTrack
	}

	private renderShape = () => {
		if (!this.shapeRendered) this.showInsideTrack.asset.opacity = 1
		this.shapeRendered = true
		this.editor.canvas.requestRenderAll()
	}

	/**
	 * Applies the provided mask to the track with show inside masks
	 * @param mask
	 */
	private hideOutside = (mask: TrackMask) => {
		this.showInsideTrack.setMask(mask)
		this.renderShape()
	}

	/**
	 * Applies the provided mask to the track with show outside masks
	 * @param mask
	 */
	private hideInside = (mask: TrackMask) => {
		this.showOutsideTrack.setMask(mask)

		this.renderShape()
	}

	/**
	 * Trigger this mode and return a promise of the result
	 */
	static async CreateAsync(editor: EditorClass, options: ClickForegroundOptions) {
		const mode = editor.mode.set(this, options)

		return new Promise<ClickForegroundMode["result"]>((res, rej) => {
			mode.onexit = (m, cancelled) => {
				if (cancelled) return rej(mode.result)
				return res(mode.result)
			}
		})
	}
}

/**
 * Checks if the specified point is within the array of points.
 * @returns true if the point is inside the array of points
 */
function isPointWithin(point: fabric.Point, points: Array<fabric.Point>) {
	const x = point.x
	const y = point.y
	let inside = false

	for (let i = 0, j = points.length - 1; i < points.length; j = i++) {
		const xi = points[i].x
		const yi = points[i].y
		const xj = points[j].x
		const yj = points[j].y

		if (yi > y !== yj > y && x < ((xj - xi) * (y - yi)) / (yj - yi) + xi) {
			inside = !inside
		}
	}

	return inside
}
